import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { createBackgroundStyles } from "../../../../styles/masterStyles";
import {
  getCustomClasses,
  getCustomClassDetails,
} from "../../../../SharedData/customClassesData";

const CustomClassSection = ({ character, onChange, onCharacterUpdate, disabled = false }) => {
  const { theme } = useTheme();
  const styles = createBackgroundStyles(theme);
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [expandedFeatures, setExpandedFeatures] = useState(new Set());
  const [expandedBranches, setExpandedBranches] = useState(new Set());

  const selectedClass = character?.class || "";
  const selectedSubclass = character?.subclass || "";
  const classFeatureChoices = character?.classFeatureChoices || {};
  const characterLevel = character?.level || 1;

  // Get all custom classes except base Witch/Wizard
  const availableClasses = useMemo(() => {
    const customClasses = getCustomClasses();

    return customClasses
      .filter((cls) => cls.index !== "base_witch_wizard")
      .map((cls) => {
        const details = getCustomClassDetails(cls.index);
        return {
          ...cls,
          ...details,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  useEffect(() => {
    if (selectedClass) {
      setExpandedClasses((prev) => {
        const newSet = new Set(prev);
        newSet.add(selectedClass);
        return newSet;
      });
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSubclass) {
      setExpandedBranches((prev) => {
        const newSet = new Set(prev);
        newSet.add(selectedSubclass);
        return newSet;
      });
    }
  }, [selectedSubclass]);

  const handleClassToggle = (classId) => {
    const newClassValue = selectedClass === classId ? "" : classId;

    // Use bulk update if available to prevent batching issues
    if (onCharacterUpdate) {
      const updatedCharacter = {
        ...character,
        class: newClassValue,
        subclass: "", // Clear subclass when toggling class
      };
      onCharacterUpdate(updatedCharacter);
    } else {
      // Fallback to individual updates
      onChange("class", newClassValue);
      onChange("subclass", "");
    }

    // Expand the class if it was just selected
    if (newClassValue) {
      setExpandedClasses((prev) => {
        const newSet = new Set(prev);
        newSet.add(classId);
        return newSet;
      });
    }
  };

  const toggleClassExpansion = (classId) => {
    setExpandedClasses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(classId)) {
        newSet.delete(classId);
      } else {
        newSet.add(classId);
      }
      return newSet;
    });
  };

  const toggleFeatureExpansion = (featureKey) => {
    setExpandedFeatures((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(featureKey)) {
        newSet.delete(featureKey);
      } else {
        newSet.add(featureKey);
      }
      return newSet;
    });
  };

  const handleBranchToggle = (branchId) => {
    const newSubclassValue = selectedSubclass === branchId ? "" : branchId;

    // Use bulk update if available to prevent batching issues
    if (onCharacterUpdate) {
      const updatedCharacter = {
        ...character,
        subclass: newSubclassValue,
      };
      onCharacterUpdate(updatedCharacter);
    } else {
      // Fallback to individual update
      onChange("subclass", newSubclassValue);
    }

    // Expand the branch if it was just selected
    if (newSubclassValue) {
      setExpandedBranches((prev) => {
        const newSet = new Set(prev);
        newSet.add(branchId);
        return newSet;
      });
    }
  };

  const toggleBranchExpansion = (branchId) => {
    setExpandedBranches((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(branchId)) {
        newSet.delete(branchId);
      } else {
        newSet.add(branchId);
      }
      return newSet;
    });
  };

  const handleFeatureOptionChange = (featureName, optionName) => {
    const newChoices = {
      ...classFeatureChoices,
      [featureName]: optionName,
    };
    onChange("classFeatureChoices", newChoices);
  };

  const hasSelectedClass = selectedClass ? 1 : 0;

  // Count feature choices for the selected class
  const getFeatureChoiceCounts = (classData, characterLevel) => {
    if (!classData?.level_features) return { required: 0, selected: 0 };

    let required = 0;
    let selected = 0;

    classData.level_features.forEach((levelFeature) => {
      if (levelFeature.level <= characterLevel) {
        levelFeature.features.forEach((feature) => {
          if (feature.options && feature.options.length > 0) {
            required++;
            if (classFeatureChoices[feature.name]) {
              selected++;
            }
          }
        });
      }
    });

    return { required, selected };
  };

  const featureChoiceCounts = useMemo(() => {
    if (!selectedClass) return { required: 0, selected: 0 };
    const classData = getCustomClassDetails(selectedClass);
    return getFeatureChoiceCounts(classData, character?.level || 1);
  }, [selectedClass, classFeatureChoices, character?.level]);

  // Get features up to character level
  const getAccessibleFeatures = (classData, characterLevel) => {
    if (!classData.level_features) return [];
    return classData.level_features.filter((lf) => lf.level <= characterLevel);
  };

  const getLockedFeatures = (classData, characterLevel) => {
    if (!classData.level_features) return [];
    return classData.level_features.filter((lf) => lf.level > characterLevel);
  };

  // Extract branches from a class and determine accessibility
  const getBranchesForClass = (classData, characterLevel) => {
    if (!classData.branches) return { accessible: [], locked: [], minLevel: null };

    const branches = [];
    Object.entries(classData.branches).forEach(([branchName, branchData]) => {
      if (!branchData.progression) return;

      const levels = Object.keys(branchData.progression).map(Number).sort((a, b) => a - b);
      const firstLevel = levels[0];

      if (!firstLevel) return;

      const initialFeatures = branchData.progression[firstLevel] || [];

      branches.push({
        id: branchData.branchPath.toLowerCase().replace(/\s+/g, "_"),
        name: branchData.branchPath,
        tagline: branchData.tagline || "",
        level: firstLevel,
        progression: branchData.progression,
        initialFeatures: initialFeatures,
      });
    });

    const minLevel = branches.length > 0 ? Math.min(...branches.map(b => b.level)) : null;
    const accessible = branches.filter(b => b.level <= characterLevel);
    const locked = branches.filter(b => b.level > characterLevel);

    return { accessible, locked, minLevel };
  };

  // Get full progression tree for a branch
  const getBranchProgression = (branch, characterLevel) => {
    if (!branch.progression) return [];

    const progression = [];
    const levels = Object.keys(branch.progression).map(Number).sort((a, b) => a - b);

    levels.forEach((level) => {
      const features = branch.progression[level] || [];
      if (features.length > 0) {
        progression.push({
          level: level,
          features: features,
          isAccessible: level <= characterLevel,
        });
      }
    });

    return progression;
  };

  return (
    <div style={styles.fieldContainer}>
      {hasSelectedClass === 1 && (
        <div style={styles.completionMessage}>
          ✓ Class selected:{" "}
          {availableClasses.find((cls) => cls.id === selectedClass)?.name ||
            selectedClass}
          {featureChoiceCounts.required > 0 && (
            <span
              style={{
                marginLeft: "12px",
                padding: "4px 10px",
                backgroundColor:
                  featureChoiceCounts.selected === featureChoiceCounts.required
                    ? `${theme.success}20`
                    : `${theme.warning}20`,
                borderRadius: "12px",
                fontSize: "13px",
                fontWeight: "600",
                color:
                  featureChoiceCounts.selected === featureChoiceCounts.required
                    ? theme.success
                    : theme.warning,
                border: `1px solid ${
                  featureChoiceCounts.selected === featureChoiceCounts.required
                    ? theme.success
                    : theme.warning
                }50`,
              }}
            >
              Feature Choices: {featureChoiceCounts.selected}/
              {featureChoiceCounts.required}
            </span>
          )}
        </div>
      )}

      <div style={styles.availableElementsSection}>
        <div style={styles.availableElementsContainer}>
          {availableClasses
            .filter((classData) => {
              // If a class is selected, only show that class
              if (selectedClass) {
                return classData.id === selectedClass;
              }
              // Otherwise, show all classes
              return true;
            })
            .map((classData) => {
            const isSelected = selectedClass === classData.id;
            const isExpanded = expandedClasses.has(classData.id);
            const accessibleFeatures = getAccessibleFeatures(
              classData,
              character?.level || 1
            );
            const lockedFeatures = getLockedFeatures(
              classData,
              character?.level || 1
            );

            return (
              <div
                key={classData.id}
                style={
                  isSelected ? styles.selectedElementCard : styles.featCard
                }
              >
                <div style={styles.featHeader}>
                  <label
                    style={styles.featLabelClickable}
                    onClick={(e) => {
                      // Prevent double-firing when clicking the checkbox itself
                      if (e.target.tagName === "INPUT") return;
                      handleClassToggle(classData.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      name="class"
                      value={classData.id}
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleClassToggle(classData.id);
                      }}
                      disabled={disabled}
                      style={{
                        width: "18px",
                        height: "18px",
                        marginRight: "8px",
                        cursor: disabled ? "not-allowed" : "pointer",
                        accentColor: theme.success,
                        transform: "scale(1.2)",
                      }}
                    />
                    <span
                      style={
                        isSelected ? styles.featNameSelected : styles.featName
                      }
                    >
                      {classData.name}
                      {classData.type && (
                        <span
                          style={{
                            ...styles.availableChoicesIndicator,
                            marginLeft: "8px",
                          }}
                        >
                          ({classData.type})
                        </span>
                      )}
                    </span>
                  </label>
                  {!isSelected && (
                    <button
                      onClick={() => toggleClassExpansion(classData.id)}
                      style={styles.expandButton}
                      type="button"
                      disabled={disabled}
                    >
                      {isExpanded ? "▲" : "▼"}
                    </button>
                  )}
                </div>

                <div style={styles.featPreview}>{classData.description}</div>

                {(isExpanded || isSelected) && (
                  <div
                    style={
                      isSelected
                        ? styles.featDescriptionSelected
                        : styles.featDescription
                    }
                  >
                    {/* Class Stats Card */}
                    <div
                      style={{
                        backgroundColor: isSelected
                          ? theme.backgroundAlt
                          : theme.background,
                        border: `1px solid ${
                          isSelected ? theme.success : theme.border
                        }`,
                        borderRadius: "8px",
                        padding: "12px",
                        marginBottom: "16px",
                      }}
                    >
                        <h4
                          style={{
                            margin: "0 0 12px 0",
                            color: isSelected ? theme.success : theme.text,
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          Class Statistics
                        </h4>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "8px",
                            fontSize: "14px",
                          }}
                        >
                          <div>
                            <strong
                              style={{
                                color: isSelected
                                  ? theme.success
                                  : theme.textSecondary,
                              }}
                            >
                              Hit Die:
                            </strong>{" "}
                            <span
                              style={{
                                color: isSelected ? theme.text : theme.text,
                              }}
                            >
                              {classData.hit_die}
                            </span>
                          </div>

                          {classData.primary_abilities && (
                            <div>
                              <strong
                                style={{
                                  color: isSelected
                                    ? theme.success
                                    : theme.textSecondary,
                                }}
                              >
                                Primary:
                              </strong>{" "}
                              <span
                                style={{
                                  color: isSelected ? theme.text : theme.text,
                                }}
                              >
                                {classData.primary_abilities.join(", ")}
                              </span>
                            </div>
                          )}

                          {classData.saving_throws && (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <strong
                                style={{
                                  color: isSelected
                                    ? theme.success
                                    : theme.textSecondary,
                                }}
                              >
                                Saving Throws:
                              </strong>{" "}
                              <span
                                style={{
                                  color: isSelected ? theme.text : theme.text,
                                }}
                              >
                                {classData.saving_throws.join(", ")}
                              </span>
                            </div>
                          )}

                          {classData.skill_choices && (
                            <div style={{ gridColumn: "1 / -1" }}>
                              <strong
                                style={{
                                  color: isSelected
                                    ? theme.success
                                    : theme.textSecondary,
                                }}
                              >
                                Skills:
                              </strong>{" "}
                              <span
                                style={{
                                  color: isSelected ? theme.text : theme.text,
                                }}
                              >
                                Choose {classData.skill_choices.choose} from{" "}
                                {classData.skill_choices.options.join(", ")}
                              </span>
                            </div>
                          )}
                        </div>
                    </div>

                    {/* Class Resources */}
                    {classData.class_resources && classData.class_resources.length > 0 && (
                      <div
                        style={{
                          backgroundColor: isSelected
                            ? theme.backgroundAlt
                            : theme.background,
                          border: `1px solid ${
                            isSelected ? theme.success : theme.border
                          }`,
                          borderRadius: "8px",
                          padding: "12px",
                          marginBottom: "16px",
                        }}
                      >
                        <h4
                          style={{
                            margin: "0 0 12px 0",
                            color: isSelected ? theme.success : theme.text,
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          Class Resources
                        </h4>
                        {classData.class_resources.map((resource, idx) => (
                          <div
                            key={idx}
                            style={{
                              backgroundColor: isSelected
                                ? theme.background
                                : theme.backgroundAlt,
                              border: `1px solid ${
                                isSelected ? theme.success : theme.border
                              }`,
                              borderRadius: "8px",
                              padding: "14px",
                              marginBottom: idx < classData.class_resources.length - 1 ? "12px" : "0",
                            }}
                          >
                            <strong
                              style={{
                                color: isSelected ? theme.success : theme.primary,
                                fontSize: "15px",
                                display: "block",
                                marginBottom: "8px",
                              }}
                            >
                              {resource.name}
                            </strong>
                            <div
                              style={{
                                color: theme.text,
                                fontSize: "14px",
                                lineHeight: "1.6",
                                marginBottom: resource.surge_table ? "12px" : "0",
                              }}
                            >
                              {resource.description}
                            </div>

                            {/* Formula / recharge / duration meta */}
                            {(resource.pool_formula ||
                              resource.formula ||
                              resource.uses_formula ||
                              resource.known_formula ||
                              resource.recharge ||
                              resource.recovery ||
                              resource.duration) && (
                              <div
                                style={{
                                  fontSize: "13px",
                                  lineHeight: "1.6",
                                  marginBottom: "8px",
                                }}
                              >
                                {(resource.pool_formula ||
                                  resource.formula ||
                                  resource.uses_formula ||
                                  resource.known_formula) && (
                                  <div style={{ color: theme.textSecondary }}>
                                    <strong style={{ color: theme.text }}>
                                      Pool / Uses:
                                    </strong>{" "}
                                    {resource.pool_formula ||
                                      resource.formula ||
                                      resource.uses_formula ||
                                      resource.known_formula}
                                  </div>
                                )}
                                {(resource.recharge || resource.recovery) && (
                                  <div style={{ color: theme.textSecondary }}>
                                    <strong style={{ color: theme.text }}>
                                      Recharge:
                                    </strong>{" "}
                                    {resource.recharge || resource.recovery}
                                  </div>
                                )}
                                {resource.duration && (
                                  <div style={{ color: theme.textSecondary }}>
                                    <strong style={{ color: theme.text }}>
                                      Duration:
                                    </strong>{" "}
                                    {resource.duration}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* effects_summary / modes / rules / spend_examples */}
                            {[
                              { label: "Effects", items: resource.effects_summary },
                              { label: "Modes", items: resource.modes },
                              { label: "Rules", items: resource.rules },
                              { label: "Spend on", items: resource.spend_examples },
                            ].map(
                              (group) =>
                                Array.isArray(group.items) &&
                                group.items.length > 0 && (
                                  <div
                                    key={group.label}
                                    style={{ marginBottom: "8px" }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: theme.primary,
                                        marginBottom: "4px",
                                      }}
                                    >
                                      {group.label}
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: "18px" }}>
                                      {group.items.map((item, i) => (
                                        <li
                                          key={i}
                                          style={{
                                            fontSize: "13px",
                                            color: theme.text,
                                            lineHeight: "1.5",
                                            marginBottom: "2px",
                                          }}
                                        >
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                            )}

                            {/* notes (string or array) */}
                            {resource.notes && (
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: theme.textSecondary,
                                  lineHeight: "1.6",
                                  fontStyle: "italic",
                                  marginBottom: "8px",
                                }}
                              >
                                {Array.isArray(resource.notes)
                                  ? resource.notes.join(" ")
                                  : resource.notes}
                              </div>
                            )}

                            {/* Surge Table Display */}
                            {resource.surge_table && (
                              <div
                                style={{
                                  marginTop: "12px",
                                  backgroundColor: theme.surface,
                                  border: `1px solid ${theme.border}`,
                                  borderRadius: "6px",
                                  padding: "12px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: theme.primary,
                                    marginBottom: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  🎲 Surge Table ({resource.surge_roll})
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                  }}
                                >
                                  {resource.surge_table.map((entry, entryIdx) => {
                                    const parts = entry.split(" — ");
                                    const roll = parts[0];
                                    const effect = parts[1] || entry;

                                    return (
                                      <div
                                        key={entryIdx}
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          fontSize: "12px",
                                          padding: "6px 8px",
                                          backgroundColor: theme.background,
                                          borderRadius: "4px",
                                          border: `1px solid ${theme.border}`,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontWeight: "700",
                                            color: theme.primary,
                                            minWidth: "40px",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {roll}
                                        </span>
                                        <span
                                          style={{
                                            color: theme.text,
                                            lineHeight: "1.4",
                                          }}
                                        >
                                          {effect}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Uses */}
                            {resource.uses && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  fontSize: "12px",
                                  color: theme.textSecondary,
                                  fontStyle: "italic",
                                }}
                              >
                                Uses: {resource.uses}
                              </div>
                            )}

                            {/* Strain scaling */}
                            {resource.strain && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  fontSize: "12px",
                                  color: theme.textSecondary,
                                }}
                              >
                                <strong>Strain:</strong>
                                {Object.entries(resource.strain).map(([level, damage], idx) => (
                                  <span key={level}>
                                    {idx > 0 && ", "}
                                    {" "}Level {level}: {damage}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Branch Selection - Class Specializations */}
                    {(() => {
                      const { accessible, locked, minLevel } = getBranchesForClass(classData, characterLevel);

                      if (accessible.length === 0 && locked.length === 0) {
                        return null; // No branches for this class
                      }

                      return (
                        <div
                          style={{
                            backgroundColor: isSelected
                              ? theme.backgroundAlt
                              : theme.background,
                            border: `1px solid ${
                              isSelected ? theme.success : theme.border
                            }`,
                            borderRadius: "8px",
                            padding: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          <h4
                            style={{
                              margin: "0 0 8px 0",
                              color: isSelected ? theme.success : theme.text,
                              fontSize: "16px",
                              fontWeight: "600",
                            }}
                          >
                            Class Specialization
                          </h4>
                          <div
                            style={{
                              fontSize: "13px",
                              color: theme.textSecondary,
                              marginBottom: "12px",
                              lineHeight: "1.5",
                            }}
                          >
                            Choose your specialization path within the {classData.name} class. Each specialization provides unique features and abilities that unlock as you gain levels, typically at levels 3, 5, 7, 9, 13, and 17. Expand a specialization to see its full progression tree and all features you'll gain.
                            {characterLevel < minLevel && accessible.length === 0 && (
                              <span
                                style={{
                                  display: "block",
                                  marginTop: "4px",
                                  color: theme.warning,
                                  fontStyle: "italic",
                                }}
                              >
                                ⚠️ Specializations become available at level {minLevel}. Your character is currently level {characterLevel}.
                              </span>
                            )}
                          </div>

                          {selectedSubclass && (
                            <div
                              style={{
                                padding: "8px 12px",
                                backgroundColor: `${theme.success}15`,
                                border: `1px solid ${theme.success}50`,
                                borderRadius: "6px",
                                marginBottom: "12px",
                                fontSize: "13px",
                                color: theme.success,
                                fontWeight: "500",
                              }}
                            >
                              ✓ Specialization selected: {accessible.find(b => b.id === selectedSubclass)?.name || selectedSubclass}
                            </div>
                          )}

                          {/* Accessible Branches */}
                          {accessible.length > 0 && (
                            <div style={{ marginBottom: locked.length > 0 ? "16px" : "0" }}>
                              {accessible.map((branch) => {
                                const isBranchSelected = selectedSubclass === branch.id;
                                const isBranchExpanded = expandedBranches.has(branch.id);

                                return (
                                  <div
                                    key={branch.id}
                                    style={{
                                      marginBottom: "12px",
                                      backgroundColor: isBranchSelected
                                        ? `${theme.success}10`
                                        : isSelected
                                        ? theme.background
                                        : theme.backgroundAlt,
                                      border: `2px solid ${
                                        isBranchSelected ? theme.success : theme.border
                                      }`,
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "12px",
                                      }}
                                    >
                                      <label
                                        style={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          flex: 1,
                                          cursor: disabled ? "not-allowed" : "pointer",
                                        }}
                                        onClick={(e) => {
                                          if (e.target.tagName !== "INPUT" && !disabled) {
                                            handleBranchToggle(branch.id);
                                          }
                                        }}
                                      >
                                        <input
                                          type="radio"
                                          name={`${classData.id}-branch`}
                                          value={branch.id}
                                          checked={isBranchSelected}
                                          onChange={() => handleBranchToggle(branch.id)}
                                          disabled={disabled}
                                          style={{
                                            width: "18px",
                                            height: "18px",
                                            marginRight: "10px",
                                            marginTop: "2px",
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            accentColor: theme.success,
                                            flexShrink: 0,
                                          }}
                                        />
                                        <div style={{ flex: 1 }}>
                                          <div
                                            style={{
                                              fontSize: "14px",
                                              fontWeight: "600",
                                              color: isBranchSelected
                                                ? theme.success
                                                : isSelected
                                                ? theme.text
                                                : theme.primary,
                                              marginBottom: branch.tagline ? "4px" : "0",
                                            }}
                                          >
                                            {branch.name}
                                          </div>
                                          {branch.tagline && (
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                color: theme.textSecondary,
                                                fontStyle: "italic",
                                                lineHeight: "1.4",
                                              }}
                                            >
                                              {branch.tagline}
                                            </div>
                                          )}
                                        </div>
                                      </label>
                                      {!isBranchSelected && (
                                        <button
                                          onClick={() => toggleBranchExpansion(branch.id)}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px 8px",
                                            color: theme.primary,
                                            fontSize: "14px",
                                          }}
                                          type="button"
                                        >
                                          {isBranchExpanded ? "▲" : "▼"}
                                        </button>
                                      )}
                                    </div>

                                    {/* Branch Progression Tree */}
                                    {(isBranchExpanded || isBranchSelected) && (
                                      <div
                                        style={{
                                          borderTop: `1px solid ${theme.border}`,
                                          padding: "12px",
                                        }}
                                      >
                                        {getBranchProgression(branch, characterLevel).map(
                                          (progressionLevel, progIndex) => (
                                            <div key={`prog-${progIndex}`}>
                                              {/* Level header within the branch */}
                                              <div
                                                style={{
                                                  fontSize: "13px",
                                                  fontWeight: "700",
                                                  color: progressionLevel.isAccessible
                                                    ? isBranchSelected
                                                      ? theme.success
                                                      : theme.primary
                                                    : theme.textSecondary,
                                                  marginTop: progIndex > 0 ? "16px" : "0",
                                                  marginBottom: "8px",
                                                  paddingBottom: "4px",
                                                  borderBottom: `1px solid ${
                                                    progressionLevel.isAccessible
                                                      ? theme.border
                                                      : theme.border + "50"
                                                  }`,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                  opacity: progressionLevel.isAccessible ? 1 : 0.6,
                                                }}
                                              >
                                                {!progressionLevel.isAccessible && "🔒 "}
                                                Level {progressionLevel.level} Features
                                                {!progressionLevel.isAccessible && (
                                                  <span
                                                    style={{
                                                      fontSize: "11px",
                                                      fontWeight: "500",
                                                      padding: "2px 6px",
                                                      backgroundColor: `${theme.warning}15`,
                                                      borderRadius: "8px",
                                                      border: `1px solid ${theme.warning}50`,
                                                      color: theme.warning,
                                                    }}
                                                  >
                                                    Unlocks in{" "}
                                                    {progressionLevel.level - characterLevel} level
                                                    {progressionLevel.level - characterLevel > 1
                                                      ? "s"
                                                      : ""}
                                                  </span>
                                                )}
                                                {progressionLevel.isAccessible &&
                                                  progressionLevel.level === characterLevel && (
                                                    <span
                                                      style={{
                                                        fontSize: "11px",
                                                        fontWeight: "600",
                                                        color: theme.success,
                                                        padding: "2px 8px",
                                                        backgroundColor: `${theme.success}15`,
                                                        borderRadius: "8px",
                                                        border: `1px solid ${theme.success}50`,
                                                      }}
                                                    >
                                                      ✓ CURRENT
                                                    </span>
                                                  )}
                                              </div>

                                              {/* Features for this level */}
                                              {progressionLevel.features.map(
                                                (feature, featureIndex) => (
                                                  <div
                                                    key={featureIndex}
                                                    style={{
                                                      padding: "10px",
                                                      marginBottom: "8px",
                                                      backgroundColor: progressionLevel.isAccessible
                                                        ? isBranchSelected
                                                          ? theme.background
                                                          : theme.backgroundAlt
                                                        : `${theme.surface}80`,
                                                      border: `1px solid ${
                                                        progressionLevel.isAccessible
                                                          ? theme.border
                                                          : theme.border + "50"
                                                      }`,
                                                      borderRadius: "6px",
                                                      opacity: progressionLevel.isAccessible ? 1 : 0.5,
                                                    }}
                                                  >
                                                    <strong
                                                      style={{
                                                        fontSize: "13px",
                                                        fontWeight: "600",
                                                        color: progressionLevel.isAccessible
                                                          ? isBranchSelected
                                                            ? theme.success
                                                            : theme.text
                                                          : theme.textSecondary,
                                                        display: "block",
                                                        marginBottom: "4px",
                                                      }}
                                                    >
                                                      {feature.name}
                                                    </strong>
                                                    <p
                                                      style={{
                                                        fontSize: "12px",
                                                        color: progressionLevel.isAccessible
                                                          ? theme.textSecondary
                                                          : theme.textSecondary,
                                                        lineHeight: "1.4",
                                                        margin: 0,
                                                      }}
                                                    >
                                                      {feature.description}
                                                    </p>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Locked Branches */}
                          {locked.length > 0 && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: theme.textSecondary,
                                  marginBottom: "8px",
                                  paddingTop: accessible.length > 0 ? "12px" : "0",
                                  borderTop: accessible.length > 0 ? `1px dashed ${theme.border}` : "none",
                                }}
                              >
                                🔒 Locked Specializations
                              </div>
                              {locked.map((branch) => (
                                <div
                                  key={branch.id}
                                  style={{
                                    padding: "10px 12px",
                                    marginBottom: "8px",
                                    backgroundColor: theme.backgroundAlt,
                                    border: `1px dashed ${theme.border}`,
                                    borderRadius: "6px",
                                    opacity: 0.6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: theme.textSecondary,
                                      marginBottom: "2px",
                                    }}
                                  >
                                    🔒 {branch.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: theme.textSecondary,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    Unlocks at level {branch.level}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Accessible features - Grouped by level */}
                    <div style={{ marginBottom: "16px" }}>
                      {accessibleFeatures.map((levelFeature, levelIndex) => (
                        <div
                          key={`level-${levelFeature.level}`}
                          style={{
                            marginBottom: levelIndex < accessibleFeatures.length - 1 ? "24px" : "0",
                          }}
                        >
                          {/* Level Header */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginBottom: "12px",
                              paddingBottom: "8px",
                              borderBottom: `2px solid ${isSelected ? theme.success : theme.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "700",
                                color: isSelected ? theme.success : theme.primary,
                              }}
                            >
                              Level {levelFeature.level} Features
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: theme.textSecondary,
                                padding: "2px 8px",
                                backgroundColor: theme.surface,
                                borderRadius: "12px",
                                border: `1px solid ${theme.border}`,
                              }}
                            >
                              {levelFeature.features.length} feature{levelFeature.features.length > 1 ? "s" : ""}
                            </div>
                          </div>

                          {/* Features list */}
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {levelFeature.features
                              .filter((feature) => {
                                // Filter out branch progression features
                                return !feature.from_branch &&
                                       !feature.branch_milestone &&
                                       !feature.isAutomaticFromBranch &&
                                       !feature.name?.includes("Branch Progression") &&
                                       !feature.name?.includes("Department Feature") &&
                                       !feature.name?.includes("Beat Progression") &&
                                       !feature.name?.includes("Discipline Progression") &&
                                       !feature.name?.includes("Mandate Progression");
                              })
                              .map((feature, index) => (
                              <div
                                key={`level-${levelFeature.level}-${index}`}
                                style={{
                                  backgroundColor: isSelected
                                    ? theme.background
                                    : theme.backgroundAlt,
                                  border: `1px solid ${
                                    isSelected ? theme.success : theme.border
                                  }`,
                                  borderRadius: "8px",
                                  padding: "14px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <strong
                                    style={{
                                      color: isSelected
                                        ? theme.success
                                        : theme.text,
                                      fontSize: "14px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {feature.name}
                                  </strong>
                                </div>
                                <p
                                  style={{
                                    color: isSelected
                                      ? theme.text
                                      : theme.textSecondary,
                                    fontSize: "13px",
                                    lineHeight: "1.5",
                                    margin: 0,
                                  }}
                                >
                                  {feature.description}
                                </p>

                                {/* Display feature options if they exist */}
                                {feature.options && feature.options.length > 0 && (
                                  <div
                                    style={{
                                      marginTop: "12px",
                                      paddingTop: "12px",
                                      borderTop: `1px solid ${
                                        isSelected ? theme.border : theme.border + "80"
                                      }`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: isSelected ? theme.success : theme.primary,
                                        marginBottom: "8px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span>Choose one option:</span>
                                      {classFeatureChoices[feature.name] && (
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            fontWeight: "500",
                                            color: theme.success,
                                            backgroundColor: `${theme.success}15`,
                                            padding: "2px 8px",
                                            borderRadius: "8px",
                                            border: `1px solid ${theme.success}50`,
                                          }}
                                        >
                                          ✓ Selected
                                        </span>
                                      )}
                                    </div>
                                    {feature.options.map((option, optIndex) => {
                                      const featureKey = `${classData.id}-${levelFeature.level}-${feature.name}`;
                                      const isFeatureExpanded = expandedFeatures.has(featureKey + '-' + optIndex);
                                      const isOptionSelected = classFeatureChoices[feature.name] === option.name;

                                      return (
                                        <div
                                          key={optIndex}
                                          style={{
                                            marginBottom: "8px",
                                            backgroundColor: isOptionSelected
                                              ? `${theme.success}10`
                                              : isSelected
                                              ? theme.backgroundAlt
                                              : theme.background,
                                            borderRadius: "6px",
                                            border: `2px solid ${
                                              isOptionSelected
                                                ? theme.success
                                                : theme.border
                                            }`,
                                            overflow: "hidden",
                                            transition: "all 0.2s",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              alignItems: "flex-start",
                                              padding: "10px",
                                              cursor: disabled ? "not-allowed" : "pointer",
                                            }}
                                            onClick={(e) => {
                                              if (e.target.tagName !== "INPUT") {
                                                handleFeatureOptionChange(feature.name, option.name);
                                              }
                                            }}
                                          >
                                            <input
                                              type="radio"
                                              name={`${classData.id}-${feature.name}`}
                                              value={option.name}
                                              checked={isOptionSelected}
                                              onChange={() => handleFeatureOptionChange(feature.name, option.name)}
                                              disabled={disabled}
                                              style={{
                                                width: "18px",
                                                height: "18px",
                                                marginRight: "10px",
                                                marginTop: "2px",
                                                cursor: disabled ? "not-allowed" : "pointer",
                                                accentColor: theme.success,
                                                flexShrink: 0,
                                              }}
                                            />
                                            <div style={{ flex: 1 }}>
                                              <div
                                                style={{
                                                  fontSize: "13px",
                                                  fontWeight: "600",
                                                  color: isOptionSelected
                                                    ? theme.success
                                                    : isSelected
                                                    ? theme.text
                                                    : theme.primary,
                                                  marginBottom: option.tagline ? "4px" : "4px",
                                                }}
                                              >
                                                {option.name}
                                              </div>
                                              {option.tagline && (
                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    color: theme.textSecondary,
                                                    fontStyle: "italic",
                                                    marginBottom: "6px",
                                                    lineHeight: "1.3",
                                                  }}
                                                >
                                                  {option.tagline}
                                                </div>
                                              )}
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  color: theme.textSecondary,
                                                  lineHeight: "1.4",
                                                  display: option.description ? "block" : "none",
                                                }}
                                              >
                                                {option.description}
                                              </div>
                                            </div>
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Locked features */}
                    {lockedFeatures.length > 0 && (
                      <div
                        style={{
                          backgroundColor: theme.backgroundAlt,
                          border: `1px dashed ${theme.border}`,
                          borderRadius: "8px",
                          padding: "12px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            color: theme.textSecondary,
                            fontSize: "14px",
                          }}
                        >
                          🔒 {lockedFeatures.length} feature level
                          {lockedFeatures.length > 1 ? "s" : ""} unlock at
                          higher character levels (
                          {lockedFeatures.map((lf) => lf.level).join(", ")})
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={styles.helpText}>
        Note: All characters also have the base Witch / Wizard class
        automatically. Choose a subclass to specialize your magical
        training.
        {selectedClass && (
          <span
            style={{
              display: "block",
              marginTop: "4px",
              fontStyle: "italic",
              color: theme.success,
            }}
          >
            Uncheck the selected class to see all available options again.
          </span>
        )}
      </div>
    </div>
  );
};

export default CustomClassSection;
